import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, security } from '../lib/api';

export interface FeatureFlags {
  premiumTempUnlocked: boolean;
  billingGateEnabled: boolean;
  billingModalEnabled: boolean;
  mfaSecurityEnabled: boolean;
}

export interface EmailPreferences {
  monthlyReportsEnabled: boolean;
}

export interface User {
  userId: string;
  email: string;
  name: string | null;
  /** Must match Plan type in packages/types/src/index.ts */
  plan: 'free' | 'pro' | 'enterprise';
  /** Max workspaces this user can own on their current plan */
  workspaceLimit: number;
  /** Number of workspaces this user currently owns */
  workspaceCount: number;
  /** Whether TOTP MFA is enabled for this user */
  mfaEnabled: boolean;
  featureFlags?: FeatureFlags;
  emailPreferences?: EmailPreferences;
  preferredCurrency?: 'GBP' | 'USD' | 'EUR' | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ mfaRequired?: true; session?: string }>;
  completeMfaLogin: (session: string, code: string, email: string) => Promise<void>;
  signup: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'plevits_id_token';
const ACCESS_TOKEN_KEY = 'plevits_access_token';
const REFRESH_TOKEN_KEY = 'plevits_refresh_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  // Start loading as true if we have a token - we need to verify it
  const [isLoading, setIsLoading] = useState(() => {
    return !!localStorage.getItem(TOKEN_KEY);
  });

  const fetchUser = async () => {
    try {
      const response = await api.get<User>('/auth/me');
      setUser(response);
    } catch (error: unknown) {
      // Only clear tokens on authentication errors (401)
      const apiError = error as { status?: number };
      if (apiError.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setUser(null);
      }
      // For other errors (network, 500, etc.), keep the user logged in
      // They might just have a temporary connectivity issue
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      fetchUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string): Promise<{ mfaRequired?: true; session?: string }> => {
    const response = await api.post<{
      accessToken?: string;
      refreshToken?: string;
      idToken?: string;
      challengeName?: string;
      session?: string;
    }>('/auth/login', { email, password });

    if (response.challengeName === 'SOFTWARE_TOKEN_MFA' && response.session) {
      return { mfaRequired: true, session: response.session };
    }

    localStorage.setItem(TOKEN_KEY, response.idToken!);
    localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken!);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken!);

    await fetchUser();
    return {};
  };

  const completeMfaLogin = async (session: string, code: string, email: string) => {
    const response = await security.mfaChallenge(session, code, email);

    localStorage.setItem(TOKEN_KEY, response.idToken);
    localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refreshToken);

    await fetchUser();
  };

  const signup = async (
    email: string,
    password: string,
    name?: string
  ) => {
    await api.post('/auth/signup', { email, password, name });
    // After signup, user needs to verify email before logging in
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        completeMfaLogin,
        signup,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
